import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { CamposDeFacturacion } from "@/components/facturacion/CamposDeFacturacion";
import {
  FACTURACION_VACIA,
  facturacionSchema,
  type FacturacionValues,
} from "@/components/facturacion/esquemaDeFacturacion";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { useComunasDelPortal, useGuardarFacturacion } from "@/hooks/usePortalPublico";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import type { FacturacionDelPortal } from "@/lib/portalPublico";

/**
 * Los datos con los que se le va a facturar al cliente.
 *
 * ══ POR QUÉ ESTA SECCIÓN EXISTE ═════════════════════════════════════════════
 *
 * Porque el presupuesto que el cliente firmó ya se lo prometió: los dos textos
 * de cierre del cuadro de honorarios dicen que "en caso de aceptación se
 * requerirán datos de facturación". No es un formulario que inventamos acá —es
 * la contraparte de algo que el papel afirma.
 *
 * ══ POR QUÉ ES UNA SECCIÓN Y NO UN PASO DEL MISMO FORMULARIO ════════════════
 *
 * Porque adentro de la empresa del cliente esto lo hace OTRA persona. Los
 * papeles del predio los junta quien conoce la propiedad; el RUT y el giro los
 * tiene contabilidad. Por eso el correo trae dos botones y cada uno cae
 * directo en su sección: obligar a pasar por la otra es cómo un correo termina
 * reenviado y una semana perdida.
 *
 * ══ EL CORREO DE FACTURACIÓN NO ES EL DE CONTACTO ═══════════════════════════
 *
 * `informes.email_contacto` es el interlocutor comercial —quien pidió la
 * tasación—; este suele ser contabilidad. En las empresas medianas nunca es la
 * misma persona, y mandar el documento tributario al contacto comercial es cómo
 * se pierde una factura. De ahí que el campo tenga su propia explicación abajo
 * en vez de precargarse con el otro.
 */
export function SeccionFacturacion({
  token,
  facturacion,
}: {
  token: string;
  facturacion: FacturacionDelPortal | null;
}) {
  // Las comunas se piden SOLO acá y no al abrir el portal: quien entra a dejar
  // papeles no tiene por qué bajarse 25 KB que no va a mirar.
  const comunas = useComunasDelPortal(token, true);
  const guardar = useGuardarFacturacion(token);

  const form = useForm<FacturacionValues>({
    resolver: zodResolver(facturacionSchema),
    defaultValues: FACTURACION_VACIA,
  });

  /* El portal llega DESPUÉS del primer render, así que el formulario nace
     vacío y hay que rellenarlo cuando la consulta responde. Con `reset` y no
     campo a campo: además vuelve a marcarlo limpio, que es lo que evita que la
     pantalla recién cargada se comporte como si tuviera cambios sin guardar. */
  const actualizadoEn = facturacion?.actualizado_en ?? null;
  useEffect(() => {
    if (!facturacion) return;

    form.reset({
      rut: facturacion.rut ?? "",
      razon_social: facturacion.razon_social ?? "",
      giro: facturacion.giro ?? "",
      direccion: facturacion.direccion ?? "",
      comuna_codigo: facturacion.comuna_codigo ?? "",
      email_facturacion: facturacion.email_facturacion ?? "",
      telefono: facturacion.telefono ?? "",
      contacto_nombre: facturacion.contacto_nombre ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualizadoEn]);

  const enVuelo = guardar.isPending && !guardar.isPaused;

  /* `requiere_factura === false` es que el cliente contestó "no" al aceptar:
     hay fila, pero SIN datos. No es "ya recibimos tus datos" (el banner de
     abajo mentiría) ni "todavía no los mandaste" (le pediríamos algo que ya
     contestó que no necesita). Se le muestra su respuesta y la salida. */
  const dijoQueNo = facturacion?.requiere_factura === false;
  const recibidos = facturacion !== null && !dijoQueNo;
  const [quiereFactura, setQuiereFactura] = useState(false);

  if (dijoQueNo && !quiereFactura) {
    return (
      <div className="space-y-4">
        <p className="text-ink-muted text-sm text-pretty">
          Indicaste que no requieres factura.
        </p>
        <Button type="button" variant="outline" size="lg" onClick={() => setQuiereFactura(true)}>
          Necesito factura
        </Button>
      </div>
    );
  }

  function handleGuardar(valores: FacturacionValues) {
    guardar.mutate(
      {
        ...valores,
        // Los dos opcionales viajan como `null` y no como cadena vacía: el
        // servidor hace un REEMPLAZO completo, así que vaciar el teléfono desde
        // la pantalla tiene que dejarlo vacío de verdad en la base.
        telefono: valores.telefono || null,
        contacto_nombre: valores.contacto_nombre || null,
      },
      {
        onSuccess: () => toast.success("Datos de facturación guardados"),
        onError: (fallo) => {
          // El 422 del servidor se pinta SOBRE los campos —el del RUT es el que
          // importa: es el único que puede decir algo que el navegador no sabe,
          // porque valida el dígito verificador.
          const errores = parseValidationErrors(fallo);
          if (errores) {
            for (const [campo, mensaje] of Object.entries(errores)) {
              form.setError(campo as keyof FacturacionValues, { message: mensaje });
            }
            return;
          }

          toast.error(
            getApiErrorMessage(fallo, "No pudimos guardar los datos. Inténtalo de nuevo."),
          );
        },
      },
    );
  }

  return (
    <Form {...form}>
      {/* `noValidate` NO es opcional acá: hay un `<input type="email">`, y la
          validación nativa del navegador corre ANTES del submit y le gana a
          zod. Sin esto, un correo mal escrito hace que el navegador cancele el
          envío y muestre su propio globo —otro idioma, otro estilo, fuera del
          DS—, `handleSubmit` nunca corre y el `FormMessage` queda vacío. En una
          pantalla que ve un cliente externo, eso es un formulario que rechaza
          sin decir por qué. */}
      <form noValidate onSubmit={form.handleSubmit(handleGuardar)} className="space-y-5">
        {recibidos ? (
          <p className="text-success-soft-foreground bg-success-soft flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold">
            <CheckCircle2Icon className="size-4 shrink-0" aria-hidden="true" />
            Ya recibimos tus datos. Puedes corregirlos cuando quieras.
          </p>
        ) : (
          <p className="text-ink-muted text-sm text-pretty">
            Con estos datos emitimos la factura del servicio. Son los que aparecen en tu e-RUT.
          </p>
        )}

        <CamposDeFacturacion form={form} comunas={comunas} />

        <Button type="submit" size="lg" disabled={enVuelo}>
          {enVuelo && <Spinner />}
          {enVuelo ? "Guardando…" : recibidos ? "Guardar cambios" : "Enviar datos"}
        </Button>
      </form>
    </Form>
  );
}
